// ============================================================
// THE CONDITIONS ON A VEHICLE'S PLATE (plan body-map S2; the vehicle studies,
// ruled 2026-10-09). As on a person, each changes the vehicle's own lines on
// a layer of its own. Outside in:
//
//   Haywire (the scan jammed) > the systems (Disabled: dim, blinking back in
//   fits; Unaware cool; Helpless dim) > Pinned (near misses) > Stunned
//   (ghosts) > On Fire (heat shimmer) > the lines (burning; Dead powers off to
//   a cold grey hulk; Explodes throws each piece out and leaves a grey wreck
//   that keeps 30% of the throw) > per location: the Turret's broken gun and
//   dark vision, the Hull's breach, the Motive cold (Immobilised), the Rear
//   throbbing (Engine Damaged).
//
// Over them: the broken gun's lines in red, the crew's hatches (a flash when
// Crew Hit arrives, then dim), the calm glow. Beside them: smoke off the deck
// (Engine Damaged thin, On Fire thick), the breach's sparking edge, the dead
// hulk's thin smoke, Helpless's one sight round the whole vehicle. Death and
// Explodes end every condition; the damage to the locations stays.
//
// The parts each effect is fitted to (guns, hatches, vision, vents, the
// breach) come with the vehicle's asset (split_vehicles.py).
// ============================================================

import type { CSSProperties, ReactNode } from 'react';
import clsx from '../../../utils/clsx';
import type { PlateState } from '../../../engine/plate/plateState';
import type { VehicleArt, VehicleLocation } from '../../../engine/plate/vehicleArt';
import { pieceThrow, piecesOf } from '../../../engine/plate/vehicleModel';
import fx from '../../../styles/PlateFx.module.css';
import { OpenSight } from '../plateParts';
import {
  CalmBand,
  Copy,
  heatLayer,
  hwLayer,
  looksStyle,
  MASK_RECT,
  pinLayer,
  Stack,
  StripeMask,
  stunLayer,
  useArrived,
  WholeBodyDefs,
  type Looks,
} from './conditions';

type Rect = [number, number, number, number];
const rectProps = ([x0, y0, x1, y1]: Rect) => ({ x: x0, y: y0, width: x1 - x0, height: y1 - y0 });

/** The vision blocks and periscopes, near-black in a mask (Blinded): the lines behind them all but gone. */
function Vision({ art }: { art: VehicleArt }) {
  return (
    <>
      {art.parts.vision.map((r, i) => (
        <rect key={`v${i}`} {...rectProps(r)} fill="black" opacity={0.93} />
      ))}
      {art.parts.periscopes.map(([cx, cy, r], i) => (
        <circle key={`p${i}`} cx={cx} cy={cy} r={r} fill="black" opacity={0.93} />
      ))}
    </>
  );
}

/** A mask over one location: white everywhere, the hidden parts black. */
function LocationMask({ u, id, children }: { u: string; id: string; children: ReactNode }) {
  return (
    <mask id={`${u}-m-${id}`} maskUnits="userSpaceOnUse" {...MASK_RECT}>
      <rect {...MASK_RECT} fill="white" />
      {children}
    </mask>
  );
}

/** The locations a condition masks: the Turret (its guns gone, its vision dark), the Hull (torn, its vision dark). */
const MASKED: Partial<Record<VehicleLocation, (s: PlateState) => boolean>> = {
  turret: (s) => s.wepDest || s.blind,
  hull: (s) => s.hullBr || s.blind,
};

/** The breach's outline as polygon points. */
const breachPoints = (art: VehicleArt) => art.parts.breach.map((q) => q.join(',')).join(' ');

/** The Turret's and the Hull's masks, when a condition masks them. */
function PartMasks({ u, s, art }: { u: string; s: PlateState; art: VehicleArt }) {
  return (
    <>
      {MASKED.turret!(s) && (
        <LocationMask u={u} id="turret">
          {s.wepDest && art.parts.guns.map((r, i) => <rect key={i} {...rectProps(r)} fill="black" />)}
          {s.blind && <Vision art={art} />}
        </LocationMask>
      )}
      {MASKED.hull!(s) && (
        <LocationMask u={u} id="hull">
          {s.hullBr && <polygon points={breachPoints(art)} fill="black" />}
          {s.blind && <Vision art={art} />}
        </LocationMask>
      )}
    </>
  );
}

/** The gun's clip and its broken stripes; the crew's hatches. */
function PartClips({ u, s, art }: { u: string; s: PlateState; art: VehicleArt }) {
  return (
    <>
      {s.wepDest && (
        <>
          <clipPath id={`${u}-c-gun`}>
            {art.parts.guns.map((r, i) => (
              <rect key={i} {...rectProps(r)} />
            ))}
          </clipPath>
          <StripeMask id={`${u}-m-gunbreak`} size={120} band={34} angle={-18} />
        </>
      )}
      {s.crewHit && (
        <clipPath id={`${u}-c-hatch`}>
          {art.parts.hatches.map(([cx, cy, r], i) => (
            <circle key={`c${i}`} cx={cx} cy={cy} r={r} />
          ))}
          {art.parts.hatchRects.map((r, i) => (
            <rect key={`r${i}`} {...rectProps(r)} />
          ))}
        </clipPath>
      )}
    </>
  );
}

/** Each drawn location's pieces, each under its own clip, so the blast throws them whole. */
function PieceClips({ u, art }: { u: string; art: VehicleArt }) {
  return Object.entries(art.regions).flatMap(([id, pieces]) =>
    pieces.map((p, k) => (
      <clipPath key={`${id}-${k}`} id={`${u}-c-${id}-${k}`}>
        <polygon points={p} />
      </clipPath>
    )),
  );
}

/** Every def the vehicle's conditions in play need. */
export function VehicleConditionDefs({ u, s, art }: { u: string; s: PlateState; art: VehicleArt }) {
  return (
    <>
      <WholeBodyDefs u={u} s={s} />
      {s.unaware && <CalmBand u={u} />}
      <PartMasks u={u} s={s} art={art} />
      <PartClips u={u} s={s} art={art} />
      {s.explodes && <PieceClips u={u} art={art} />}
    </>
  );
}

/** A vehicle's systems' look, on their own layer with Disabled's fits: Unaware cool, Helpless dim. */
const VEHICLE_LOOKS: Looks = [
  ['unaware', 'saturate(.5) hue-rotate(-28deg) brightness(.95)', 1],
  ['helpless', 'brightness(.8)', 0.65],
];

/** How the vehicle ended (`data-end`, for tests and probes): the blast playing, the wreck, the power gone. */
function endOf(s: PlateState, now: boolean) {
  if (s.explodes) return now ? 'blast' : 'wreck';
  return s.dead ? 'off' : undefined;
}

/** What the lines become, by how it ended - the power failing as it arrives, a hulk after. */
const END_CLASS = { blast: fx.exploding, wreck: fx.wreck, off: fx.powerOff };

interface LayersProps {
  u: string;
  s: PlateState;
  children: ReactNode;
  overlays?: ReactNode;
}

/**
 * The whole-vehicle layers round its lines, outside in; the overlays inside them all. Its end - the blast, the power
 * failing - plays only when it arrives on a plate already showing (else it is simply a wreck, a hulk).
 */
export function VehicleLayers({ u, s, children, overlays }: LayersProps) {
  const endNow = useArrived(s.explodes || s.dead);
  const end = endOf(s, endNow);
  const layers = [
    hwLayer(u, s),
    { className: clsx(s.disabled && fx.fits), style: looksStyle(s, VEHICLE_LOOKS) },
    pinLayer(s),
    stunLayer(u, s),
    heatLayer(u, s),
  ];
  return (
    <Stack layers={layers}>
      <g
        className={clsx(s.fire && fx.burning, end && END_CLASS[end], end === 'off' && endNow && fx.powerOffNow)}
        data-end={end}
      >
        {children}
      </g>
      {overlays}
    </Stack>
  );
}

/** A location's own condition layer: its mask, or the Motive's cold, or the Rear's failing beat. */
export function vehicleLocationLayer(u: string, s: PlateState, id: VehicleLocation) {
  const masked = MASKED[id]?.(s);
  return {
    className: clsx(id === 'motive' && s.immob && fx.cold, id === 'rear' && s.engDmg && fx.throb),
    mask: masked ? `url(#${u}-m-${id})` : undefined,
  };
}

/** A piece's throw as the CSS the blast and the wreck read. */
export function throwStyle(art: VehicleArt, id: VehicleLocation, piece: string | null): CSSProperties {
  const [x0, y0, x1, y1] = art.box;
  const t = pieceThrow(art.blow[id], piecesOf(art, id).length, piece, [(x0 + x1) / 2, (y0 + y1) / 2]);
  return { '--tx': `${t.tx}px`, '--ty': `${t.ty}px`, '--rot': `${t.rot}deg` } as CSSProperties;
}

/** The broken gun's lines in red, the crew's hatches (flashing as Crew Hit arrives), the calm glow. */
export function VehicleOverlays({ u, s }: { u: string; s: PlateState }) {
  const crewNow = useArrived(s.crewHit);
  return (
    <>
      {s.wepDest && (
        <g className={clsx(fx.gunBroken, fx.glitch)} clipPath={`url(#${u}-c-gun)`} pointerEvents="none">
          <g mask={`url(#${u}-m-gunbreak)`}>
            <use href={`#${u}-art`} fill="currentColor" />
          </g>
        </g>
      )}
      {s.crewHit && (
        <g className={clsx(fx.crew, crewNow && fx.crewNow)} clipPath={`url(#${u}-c-hatch)`} pointerEvents="none">
          <use href={`#${u}-art`} fill="currentColor" />
        </g>
      )}
      {s.unaware && <Copy u={u} clip="calm" colour="--plate-calm-glow" opacity={0.55} />}
    </>
  );
}

/** One smoke ring: where it rises from, how big, how far into its loop it starts, which way it drifts. */
interface Ring {
  key: string;
  at: readonly [number, number];
  r: number;
  delay: number;
  drift: number;
}

const ringStyle = (ring: Ring) =>
  ({ animationDelay: `${ring.delay.toFixed(2)}s`, '--drift': `${ring.drift}px` }) as CSSProperties;

function SmokeRing({ ring, className }: { ring: Ring; className: string }) {
  return <circle className={className} cx={ring.at[0]} cy={ring.at[1]} r={ring.r} style={ringStyle(ring)} />;
}

/** The vents' rings: Engine Damaged one each, thin; On Fire two each, thick - started at staggered points. */
function smokeRings(vents: readonly (readonly [number, number])[], fire: boolean): Ring[] {
  const per = fire ? 2 : 1;
  return vents.flatMap((at, i) =>
    Array.from({ length: per }, (_, k) => ({
      key: `${i}-${k}`,
      at,
      r: fire ? 46 : 34,
      delay: -((i * 0.53 + k * 1.6) % 3.2),
      drift: (i % 2 ? 1 : -1) * (30 + 20 * k),
    })),
  );
}

/** The dead hulk's thin smoke off its own vents, starting once the power has gone. */
const smoulder = (vents: readonly (readonly [number, number])[]): Ring[] =>
  vents.map((at, i) => ({ key: `s${i}`, at, r: 30, delay: 2.6 + i * 1.3, drift: i % 2 ? 40 : -40 }));

/** The breach's torn edge, sparking. */
function Breach({ art }: { art: VehicleArt }) {
  const pts = art.parts.breach;
  return (
    <>
      <polygon className={fx.breachEdge} points={breachPoints(art)} />
      {pts
        .filter((_, i) => i % 2 === 0)
        .map(([x, y], i) => (
          <circle
            key={i}
            className={fx.spark}
            cx={x}
            cy={y}
            r={14}
            style={{ animationDelay: `-${(i * 0.37).toFixed(2)}s` }}
          />
        ))}
    </>
  );
}

/** What lies beside the vehicle's lines: its smoke, the breach, the hulk's smoke, Helpless's sight. */
export function VehicleExtras({ s, art }: { s: PlateState; art: VehicleArt }) {
  const smoke = s.engDmg || s.fire ? smokeRings(art.parts.smoke, s.fire) : [];
  return (
    <g pointerEvents="none">
      {s.dead &&
        smoulder(art.parts.smoke).map((ring) => (
          <SmokeRing key={ring.key} ring={ring} className={clsx(fx.puff, fx.smoulder)} />
        ))}
      {smoke.map((ring) => (
        <SmokeRing key={ring.key} ring={ring} className={clsx(fx.puff, s.fire && fx.puffWarm)} />
      ))}
      {s.hullBr && <Breach art={art} />}
      {s.helpless && <OpenSight box={art.box} margin={70} arm={110} />}
    </g>
  );
}
